import { KeenSliderInstance } from 'keen-slider'

import { useCallback, useEffect, useRef, useState } from 'react'

import { applySliderSlideStyles } from './apply-slider-slide-styles'

import { DetailModalDirection, IranianWomenItemViewModel } from '../types'

interface UseWomenCarouselParams {
    selectedCategory: string | null
    isMobile: boolean
    slides: IranianWomenItemViewModel[]
    totalSlides: number
    perView: number
    visibleSideCount: number
}

export const useWomenCarousel = ({
    selectedCategory,
    isMobile,
    slides,
    totalSlides,
    perView,
    visibleSideCount,
}: UseWomenCarouselParams) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [prevCategory, setPrevCategory] = useState(selectedCategory)
    const [details, setDetails] = useState<IranianWomenItemViewModel | null>(null)
    const [detailsDirection, setDetailsDirection] = useState<DetailModalDirection>('next')
    const sliderInstanceRef = useRef<KeenSliderInstance | null>(null)
    const carouselControlsReadyRef = useRef(false)
    const [, setCarouselControlsRevision] = useState(0)
    const [isCarouselReady, setIsCarouselReady] = useState(true)
    const isInitialCategoryEffect = useRef(true)

    if (selectedCategory !== prevCategory) {
        setPrevCategory(selectedCategory)
        setActiveIndex(0)
        sliderInstanceRef.current = null
        carouselControlsReadyRef.current = false
    }

    useEffect(() => {
        if (isInitialCategoryEffect.current) {
            isInitialCategoryEffect.current = false
            return
        }

        setIsCarouselReady(false)
        sliderInstanceRef.current = null
        carouselControlsReadyRef.current = false

        const frame = requestAnimationFrame(() => {
            setIsCarouselReady(true)
        })

        return () => cancelAnimationFrame(frame)
    }, [selectedCategory])

    useEffect(() => {
        if (!isCarouselReady) carouselControlsReadyRef.current = false
    }, [isCarouselReady])

    const handleCloseDetailsModal = () => {
        setDetails(null)
    }

    const handleDetailsChanged = useCallback(
        (slider: KeenSliderInstance) => {
            sliderInstanceRef.current = slider

            if (!carouselControlsReadyRef.current) {
                carouselControlsReadyRef.current = true
                setCarouselControlsRevision((revision) => revision + 1)
            }

            applySliderSlideStyles(slider, { visibleSideCount, isMobile })
        },
        [visibleSideCount, isMobile]
    )

    const syncSliderLayout = useCallback(() => {
        const slider = sliderInstanceRef.current

        if (!slider) return

        slider.update()
        slider.moveToIdx(0, true)
        handleDetailsChanged(slider)
    }, [handleDetailsChanged])

    useEffect(() => {
        if (!isCarouselReady) return

        let frame = 0

        frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(syncSliderLayout)
        })

        return () => cancelAnimationFrame(frame)
    }, [isCarouselReady, totalSlides, perView, isMobile, syncSliderLayout])

    const handleNavigateDetailsModal = (type: DetailModalDirection) => {
        if (!totalSlides) return

        const newIndex =
            type === 'prev' ? (activeIndex - 1 + totalSlides) % totalSlides : (activeIndex + 1) % totalSlides

        setActiveIndex(newIndex)
        setDetailsDirection(type)
        setDetails(slides[newIndex] ?? null)

        const slider = sliderInstanceRef.current
        if (slider) {
            slider.moveToIdx(newIndex)
            handleDetailsChanged(slider)
        }
    }

    return {
        activeIndex,
        setActiveIndex,
        details,
        detailsDirection,
        isCarouselReady,
        handleCloseDetailsModal,
        handleNavigateDetailsModal,
        handleDetailsChanged,
        setDetails,
    }
}
